import { useState } from 'react'
import { useForm } from '@inertiajs/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faMagnifyingGlass, faCircleCheck, faCircleXmark,
  faHashtag, faKey, faSpinner, faFileShield, faTriangleExclamation,
  faCalendarCheck, faUser, faFingerprint, faShieldHalved, faFileArrowUp, faLock, faLockOpen
} from '@fortawesome/free-solid-svg-icons'
import UserSidebar from '../../components/user/Sidebar'
import UserTopbar from '../../components/user/Topbar'

export default function UserVerifyPks({ current_user, result, last_tracking_code = '', last_code = '' }) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [fileName, setFileName] = useState('')

  const { data, setData, post, processing } = useForm({
    tracking_code:     last_tracking_code,
    verification_code: last_code,
    pdf_file:          null,
  })

  const submit = (e) => {
    e.preventDefault()
    post('/user/verify_pks/check', { preserveScroll: true, forceFormData: true })
  }

  return (
    <div className="flex h-screen bg-gray-50 font-sans overflow-hidden">
      <UserSidebar current_user={current_user} open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <UserTopbar current_user={current_user} onMenuToggle={() => setSidebarOpen(true)} placeholder="Cari laporan saya..." />

        <main className="flex-1 overflow-y-auto p-4 lg:p-8">
          <div className="max-w-2xl mx-auto">

            {/* Page header */}
            <div className="mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-600 flex items-center justify-center shadow-sm">
                  <FontAwesomeIcon icon={faFileShield} className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">Verifikasi Dokumen PKS</h1>
                  <p className="text-sm text-gray-500 mt-0.5">Periksa keaslian dokumen Perjanjian Kerahasiaan yang Anda terima</p>
                </div>
              </div>
            </div>

            {/* Info banner */}
            <div className="bg-green-50 border border-green-100 rounded-xl p-4 mb-6">
              <div className="flex gap-3">
                <FontAwesomeIcon icon={faShieldHalved} className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-green-800 space-y-1">
                  <p className="font-semibold">Cara menggunakan fitur ini</p>
                  <p>Buka file PDF PKS yang Anda unduh, lalu temukan <strong>Kode Laporan</strong> (di bagian atas) dan <strong>Kode Verifikasi</strong> (di bagian bawah dokumen bertuliskan KODE VERIFIKASI INTEGRITAS DOKUMEN). Masukkan kedua kode tersebut di bawah.</p>
                  <p className="text-green-600">Format kode verifikasi: <code className="bg-green-100 px-1 rounded font-mono">XXXX-XXXX-XXXX-XXXX</code></p>
                </div>
              </div>
            </div>

            {/* Form card */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
                <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <FontAwesomeIcon icon={faMagnifyingGlass} className="w-3.5 h-3.5 text-gray-400" />
                  Masukkan data dari dokumen PDF Anda
                </h2>
              </div>

              <form onSubmit={submit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    <FontAwesomeIcon icon={faHashtag} className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
                    Kode Laporan
                  </label>
                  <input
                    type="text"
                    value={data.tracking_code}
                    onChange={e => setData('tracking_code', e.target.value.toUpperCase())}
                    placeholder="Contoh: WBS-2024-XXXX"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                    required
                  />
                  <p className="text-xs text-gray-400 mt-1">Tertera pada bagian atas dokumen PDF</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    <FontAwesomeIcon icon={faKey} className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
                    Kode Verifikasi
                  </label>
                  <input
                    type="text"
                    value={data.verification_code}
                    onChange={e => setData('verification_code', e.target.value.toUpperCase())}
                    placeholder="Contoh: A3F1-9C02-B7E4-1D58"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                    required
                  />
                  <p className="text-xs text-gray-400 mt-1">Tertera di bagian bawah dokumen (KODE VERIFIKASI INTEGRITAS DOKUMEN)</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    <FontAwesomeIcon icon={faFileArrowUp} className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
                    Upload File PDF <span className="text-gray-400 font-normal">(opsional)</span>
                  </label>
                  <label className="flex items-center gap-3 border border-dashed border-gray-300 rounded-lg px-4 py-3 cursor-pointer hover:border-green-400 hover:bg-green-50/50 transition-all">
                    <FontAwesomeIcon icon={faFileArrowUp} className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className={`text-sm truncate ${fileName ? 'text-gray-800 font-medium' : 'text-gray-400'}`}>
                      {fileName || 'Pilih file PDF untuk cek hash SHA-256...'}
                    </span>
                    <input
                      type="file"
                      accept=".pdf"
                      className="hidden"
                      onChange={e => {
                        const file = e.target.files?.[0] || null
                        setData('pdf_file', file)
                        setFileName(file?.name || '')
                      }}
                    />
                  </label>
                  <p className="text-xs text-gray-400 mt-1">Sistem akan menghitung SHA-256 file dan membandingkan dengan hash yang tersimpan</p>
                </div>

                <button
                  type="submit"
                  disabled={processing}
                  className="w-full bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-semibold py-2.5 px-4 rounded-lg text-sm transition-colors flex items-center justify-center gap-2"
                >
                  {processing
                    ? <><FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" /> Memverifikasi...</>
                    : <><FontAwesomeIcon icon={faFingerprint} className="w-4 h-4" /> Verifikasi Dokumen</>
                  }
                </button>
              </form>
            </div>

            {/* Result card */}
            {result !== null && result !== undefined && (
              <div className={`mt-6 rounded-xl border overflow-hidden shadow-sm ${result.valid ? 'border-emerald-200' : 'border-red-200'}`}>
                <div className={`px-6 py-4 flex items-center gap-3 ${result.valid ? 'bg-emerald-600' : 'bg-red-600'}`}>
                  <FontAwesomeIcon
                    icon={result.valid ? faCircleCheck : faCircleXmark}
                    className="w-6 h-6 text-white flex-shrink-0"
                  />
                  <div>
                    <p className="text-white font-bold text-base">
                      {result.valid ? 'Dokumen ASLI dan VALID' : 'Dokumen Tidak Valid'}
                    </p>
                    <p className={`text-sm mt-0.5 ${result.valid ? 'text-emerald-100' : 'text-red-100'}`}>
                      {result.valid
                        ? 'Kode verifikasi cocok — integritas dokumen terjamin'
                        : 'Kode verifikasi tidak cocok dengan data di sistem'}
                    </p>
                  </div>
                </div>

                <div className="bg-white p-6">
                  {result.valid ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-400 mb-1 flex items-center gap-1">
                            <FontAwesomeIcon icon={faHashtag} className="w-3 h-3" /> Kode Laporan
                          </p>
                          <p className="text-sm font-bold text-gray-900 font-mono">{result.tracking_code}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-400 mb-1 flex items-center gap-1">
                            <FontAwesomeIcon icon={faUser} className="w-3 h-3" /> Nama Pelapor
                          </p>
                          <p className="text-sm font-bold text-gray-900">{result.user_name}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3 col-span-2">
                          <p className="text-xs text-gray-400 mb-1 flex items-center gap-1">
                            <FontAwesomeIcon icon={faCalendarCheck} className="w-3 h-3" /> Tanggal Persetujuan PKS
                          </p>
                          <p className="text-sm font-bold text-gray-900">{result.agreed_at}</p>
                        </div>
                        <div className="bg-emerald-50 rounded-lg p-3 col-span-2">
                          <p className="text-xs text-emerald-600 mb-1 flex items-center gap-1">
                            <FontAwesomeIcon icon={faKey} className="w-3 h-3" /> Kode Verifikasi Sistem
                          </p>
                          <p className="text-sm font-bold text-emerald-700 font-mono tracking-wider">{result.expected_code}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 pt-3 border-t border-gray-100 text-xs text-gray-400">
                        <FontAwesomeIcon icon={faShieldHalved} className="w-3.5 h-3.5" />
                        <span>Diverifikasi menggunakan HMAC-SHA256 · OpenSSL</span>
                      </div>
                      {result.hash_result?.checked && (
                        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${
                          result.hash_result.valid ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
                        }`}>
                          <FontAwesomeIcon icon={result.hash_result.valid ? faLock : faLockOpen} className="w-4 h-4 flex-shrink-0" />
                          <span>
                            {result.hash_result.valid
                              ? 'Hash SHA-256 file cocok — byte-per-byte identik dengan PDF asli'
                              : (result.hash_result.reason || 'Hash SHA-256 tidak cocok — file mungkin dimodifikasi')}
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex gap-3">
                      <FontAwesomeIcon icon={faTriangleExclamation} className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-red-700 mb-1">Verifikasi Gagal</p>
                        <p className="text-sm text-gray-600">{result.reason}</p>
                        <p className="text-xs text-gray-400 mt-3">Pastikan kode diketik persis seperti yang tercetak di dokumen PDF Anda.</p>
                        {result.hash_result?.checked && (
                          <div className={`mt-3 flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${
                            result.hash_result.valid ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
                          }`}>
                            <FontAwesomeIcon icon={result.hash_result.valid ? faLock : faLockOpen} className="w-4 h-4 flex-shrink-0" />
                            <span>
                              {result.hash_result.valid
                                ? 'Hash SHA-256 file cocok — file identik'
                                : (result.hash_result.reason || 'Hash SHA-256 tidak cocok')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

          </div>
        </main>
      </div>
    </div>
  )
}
